import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { CheckCircle2, Loader2, MailCheck, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/brand-logo";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";

const TEXTS = {
  pt: {
    deletedTitle: "Sua conta foi excluída",
    deletedBody: "Você saiu da FillSign e ninguém mais entra com esta conta. A renovação do plano foi cancelada.",
    undoUntil: (d: string) => `Mudou de ideia? Abra o link que mandamos por e-mail até ${d}.`,
    undoSoon: "Mudou de ideia? Abra o link que mandamos por e-mail nos próximos 7 dias.",
    after: "Depois disso apagamos os dados da conta. Documentos já assinados ficam guardados como prova, como a lei pede.",
    site: "Ir para o site da FillSign",
    restoring: "Desfazendo a exclusão...",
    restoredTitle: "Sua conta voltou",
    restoredBody: "A exclusão foi desfeita e você já pode entrar de novo. A renovação do plano continua cancelada: se quiser, ligue de novo em Configurações > Plano.",
    login: "Entrar",
    expiredTitle: "Este link não vale mais",
    expiredBody: "Passaram os 7 dias para desfazer, ou o link já foi usado. Se a sua conta ainda existe, é só entrar.",
    invalidTitle: "Link inválido",
    invalidBody: "Abra o link exatamente como veio no e-mail. Se não der certo, fale com o suporte.",
  },
  en: {
    deletedTitle: "Your account was deleted",
    deletedBody: "You left FillSign and nobody can sign in with this account again. The plan renewal was cancelled.",
    undoUntil: (d: string) => `Changed your mind? Open the link we sent by e-mail before ${d}.`,
    undoSoon: "Changed your mind? Open the link we sent by e-mail within 7 days.",
    after: "After that we remove the account data. Documents already signed stay stored as evidence, as the law requires.",
    site: "Go to the FillSign website",
    restoring: "Undoing the deletion...",
    restoredTitle: "Your account is back",
    restoredBody: "The deletion was undone and you can sign in again. The plan renewal stays cancelled: turn it back on in Settings > Plan if you want.",
    login: "Sign in",
    expiredTitle: "This link is no longer valid",
    expiredBody: "The 7 days to undo are over, or the link was already used. If your account still exists, just sign in.",
    invalidTitle: "Invalid link",
    invalidBody: "Open the link exactly as it came in the e-mail. If it still fails, contact support.",
  },
};

function Shell({ icon, title, body, children }: { icon: React.ReactNode; title: string; body: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 flex items-center justify-center"><BrandLogo /></div>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">{icon}</div>
          <CardTitle>{title}</CardTitle>
          <CardDescription className="space-y-2">{body}</CardDescription>
        </CardHeader>
        {children && <CardContent className="flex flex-col gap-2">{children}</CardContent>}
      </Card>
    </div>
  );
}

/** /conta/excluida: right after "Excluir minha conta" (the session is already gone). */
export function AccountDeleted() {
  const { language } = useLanguage();
  const tx = TEXTS[language === "en" ? "en" : "pt"];
  const until = new URLSearchParams(window.location.search).get("ate");
  const date = until && !Number.isNaN(Date.parse(until))
    ? new Date(until).toLocaleString(language === "en" ? "en-US" : "pt-BR", { dateStyle: "short", timeStyle: "short" })
    : null;
  return (
    <Shell
      icon={<MailCheck className="h-6 w-6 text-primary" />}
      title={tx.deletedTitle}
      body={<><span className="block">{tx.deletedBody}</span><span className="block font-medium text-foreground" data-testid="undo-until">{date ? tx.undoUntil(date) : tx.undoSoon}</span><span className="block">{tx.after}</span></>}
    >
      <Button asChild variant="outline"><a href="https://fillsign.com.br">{tx.site}</a></Button>
    </Shell>
  );
}

/** /conta/restaurar?token=...: the undo link of the e-mail. */
export function AccountRestore() {
  const { language } = useLanguage();
  const tx = TEXTS[language === "en" ? "en" : "pt"];
  const [state, setState] = useState<"working" | "ok" | "expired" | "invalid">("working");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) { setState("invalid"); return; }
    fetch(getApiUrl("/api/account/restore"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) })
      .then(async (r) => {
        if (r.ok) { setState("ok"); return; }
        const d = await r.json().catch(() => ({}));
        setState(d?.code === "expired" ? "expired" : "invalid");
      })
      .catch(() => setState("invalid"));
  }, []);

  if (state === "working") return <Shell icon={<Loader2 className="h-6 w-6 animate-spin text-primary" />} title={tx.restoring} body="" />;
  if (state === "ok")
    return (
      <Shell icon={<CheckCircle2 className="h-6 w-6 text-primary" />} title={tx.restoredTitle} body={tx.restoredBody}>
        <Button asChild data-testid="button-restored-login"><Link href="/login">{tx.login}</Link></Button>
      </Shell>
    );
  return (
    <Shell icon={<XCircle className="h-6 w-6 text-destructive" />} title={state === "expired" ? tx.expiredTitle : tx.invalidTitle} body={state === "expired" ? tx.expiredBody : tx.invalidBody}>
      <Button asChild variant="outline"><Link href="/login">{tx.login}</Link></Button>
    </Shell>
  );
}
